import { Tooltip } from "@mui/material";
import { useCallback } from "react";
import { useTenant } from "hooks/settings";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import GridViewIcon from "@mui/icons-material/GridView";
import { MultipleAnglesVideoPlayer } from "./MultipleAnglesVideoPlayer";
import { AllAnglesVideoPlayer } from "./AllAnglesVideoPlayer";
import type { MultiFeedVideoPlayerBaseProps } from "./MultiFeedVideoPlayer";
import { VideoOverlayIconButton } from "./VideoOverlayIconButton";
import { useLocalStorageState } from "hooks/useLocalStorageState";

type ViewMode = "grouped" | "all";

/**
 * Video player with toggle between grouped angles and all angles view.
 * Remembers the user's choice per storageKey, tenant, and location.
 */
export const ToggleableAnglesVideoPlayer = ({
  location,
  storageKey,
  ...rest
}: {
  location: string;
  storageKey: string;
} & MultiFeedVideoPlayerBaseProps) => {
  const tenantName = useTenant();
  const [viewMode, setViewMode] = useLocalStorageState<ViewMode>(
    `videoPlayer_${storageKey}_${tenantName}_${location}`,
    "grouped"
  );

  const toggleViewMode = useCallback(() => {
    setViewMode(viewMode === "grouped" ? "all" : "grouped");
  }, [viewMode, setViewMode]);

  const viewModeToggle = (
    <Tooltip
      title={
        viewMode === "grouped"
          ? "Switch to all angles view"
          : "Switch to grouped view"
      }
    >
      <VideoOverlayIconButton onClick={toggleViewMode}>
        {viewMode === "grouped" ? <GridViewIcon /> : <ViewCarouselIcon />}
      </VideoOverlayIconButton>
    </Tooltip>
  );

  return viewMode === "grouped" ? (
    <MultipleAnglesVideoPlayer
      location={location}
      topLeftButtons={viewModeToggle}
      {...rest}
    />
  ) : (
    <AllAnglesVideoPlayer
      location={location}
      storageKey={storageKey}
      topLeftButtons={viewModeToggle}
      {...rest}
    />
  );
};
